import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Activity, ArrowRight, History, Calendar, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PatientDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [recentDiagnoses, setRecentDiagnoses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentDiagnoses();
  }, []);

  const fetchRecentDiagnoses = async () => {
    try {
      const response = await axios.get(`${API}/diagnoses/history`);
      setRecentDiagnoses(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch diagnoses:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="patient-dashboard">
      {/* Welcome section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-10"
      >
        <span className="label-medical">{t('welcomeBackUser')}</span>
        <h1 className="text-3xl font-bold mt-2">{user?.name}</h1>
        <p className="text-muted-foreground mt-1">{t('howAreYouFeeling')}</p>
      </motion.div>

      {/* Quick action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <Card
          className="p-6 rounded-3xl border-2 border-primary/30 bg-primary/5 cursor-pointer hover:border-primary transition-all card-hover mb-8"
          onClick={() => navigate('/symptoms')}
          data-testid="start-diagnosis-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center">
                <Activity className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('startNewAssessment')}</h2>
                <p className="text-muted-foreground">{t('selectSymptomAndAnswer')}</p>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 text-primary" />
          </div>
        </Card>
      </motion.div>

      {/* Recent diagnoses */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <History className="w-5 h-5" />
            {t('recentAssessments')}
          </h2>
          {recentDiagnoses.length > 0 && (
            <Button
              variant="ghost"
              className="text-primary"
              onClick={() => navigate('/history')}
              data-testid="view-all-history"
            >
              {t('viewAll')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-5 rounded-2xl animate-pulse">
                <div className="h-5 bg-muted rounded w-1/3 mb-2" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </Card>
            ))}
          </div>
        ) : recentDiagnoses.length > 0 ? (
          <div className="space-y-3">
            {recentDiagnoses.map((diagnosis, index) => (
              <motion.div
                key={diagnosis.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Card
                  className="p-5 rounded-2xl cursor-pointer hover:bg-accent transition-all card-hover"
                  onClick={() => navigate(`/results/${diagnosis.id}`)}
                  data-testid={`diagnosis-card-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{diagnosis.symptom_name}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(diagnosis.created_at)}
                      </div>
                    </div>
                    <div className="text-right">
                      {diagnosis.possible_conditions.length > 0 && (
                        <span className="text-sm text-muted-foreground">
                          {diagnosis.possible_conditions.length} {t('conditionsFound')}
                        </span>
                      )}
                      <ChevronRight className="w-5 h-5 text-muted-foreground mt-1 ml-auto" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="p-8 rounded-2xl text-center">
            <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">{t('noAssessmentsYet')}</h3>
            <p className="text-muted-foreground text-sm mb-4">
              {t('startFirstAssessment')}
            </p>
            <Button
              className="rounded-full"
              onClick={() => navigate('/symptoms')}
              data-testid="first-assessment-btn"
            >
              {t('startAssessment')}
            </Button>
          </Card>
        )}
      </motion.div>
    </div>
  );
};
